import { CalendarCheck, MessageCircle, Sparkles, TrendingUp, Zap } from "lucide-react";

const CLOUD_NOISE = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1600' height='900'><filter id='c' x='0' y='0' width='100%25' height='100%25'><feTurbulence type='fractalNoise' baseFrequency='0.0035 0.007' numOctaves='5' seed='11'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1.9 -0.4'/></filter><rect width='100%25' height='100%25' filter='url(%23c)'/></svg>") center / cover no-repeat`;

const FLOAT_CSS = `
@keyframes auth-float {
  0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg); }
  25% { transform: translate3d(8px, -12px, 0) rotate(0.6deg); }
  50% { transform: translate3d(-6px, -20px, 0) rotate(-0.4deg); }
  75% { transform: translate3d(-10px, -8px, 0) rotate(0.5deg); }
}
@media (prefers-reduced-motion: no-preference) {
  .auth-float { animation: auth-float ease-in-out infinite; will-change: transform; }
}
`;

const WORKFLOW_STEPS = [
  { icon: MessageCircle, label: "New enquiry" },
  { icon: Sparkles, label: "AI replies" },
  { icon: CalendarCheck, label: "Booked" },
];

/**
 * Decorative brand panel for the auth page. Purely presentational: a stylised
 * slice of the admin dashboard (conversation, workflow, growth) over the landing-page
 * cloud background. Hidden below `lg`, where the form takes the whole screen.
 */
const AuthShowcase = () => (
  <aside
    aria-hidden="true"
    className="relative hidden overflow-hidden text-foreground lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-16"
    style={{ background: "linear-gradient(180deg, #f6d9d3 0%, #faeae7 100%)" }}
  >
    {/* Cloud background, same fractal-noise treatment as the landing hero */}
    <div
      className="absolute inset-0"
      style={{ background: CLOUD_NOISE }}
    />

    {/* Headline */}
    <div className="relative max-w-md">
      <div className="flex w-fit items-center gap-2">
        <img src="/dibs_assets/red_logo_with_transparent_bg.png" alt="" className="h-8 object-contain" />
        <span className="font-heading text-2xl font-bold tracking-tight text-primary">Dibs</span>
      </div>
      <h2 className="mt-6 font-heading text-3xl font-bold leading-tight xl:text-4xl">
        Every customer, one conversation away.
      </h2>
      <p className="mt-3 text-base text-muted-foreground">
        Reply, book and follow up on various channels, with Dibs AI doing the busywork.
      </p>
    </div>

    {/* Floating dashboard cards */}
    <div className="relative mx-auto my-10 flex w-full max-w-md flex-col gap-8">
      <style>{FLOAT_CSS}</style>
      <div className="auth-float" style={{ animationDuration: "7s" }}>
      <div className="rounded-xl border border-white/60 bg-white/70 p-4 shadow-card backdrop-blur-md motion-safe:animate-fade-up">
        <div className="mb-3 flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
            M
          </span>
          Mei Lin · WhatsApp
        </div>
        <div className="space-y-2 text-sm">
          <div className="max-w-[80%] rounded-lg rounded-tl-sm bg-card px-3 py-2 shadow-sm">
            Hi! Do you have a Saturday swim class for a 5 year old?
          </div>
          <div className="ml-auto max-w-[85%] rounded-lg rounded-tr-sm bg-primary px-3 py-2 text-primary-foreground">
            Yes, Saturday 10am has 3 spots left. Want me to book a trial for you?
          </div>
        </div>
      </div>

      </div>

      <div className="auth-float ml-10" style={{ animationDuration: "9s", animationDelay: "-3s" }}>
      <div
        className="rounded-xl border border-white/60 bg-white/70 p-4 shadow-card backdrop-blur-md motion-safe:animate-fade-up-delay-1"
      >
        <div className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <Zap className="h-3.5 w-3.5 text-primary" />
          Workflow
        </div>
        <div className="flex items-center">
          {WORKFLOW_STEPS.map(({ icon: Icon, label }, index) => (
            <div key={label} className="flex items-center">
              <div className="flex flex-col items-center gap-1.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-[11px] text-muted-foreground">{label}</span>
              </div>
              {index < WORKFLOW_STEPS.length - 1 && (
                <span className="mx-2 mb-5 h-px w-8 border-t border-dashed border-foreground/20" />
              )}
            </div>
          ))}
        </div>
      </div>

      </div>

      <div className="auth-float mr-10 w-52" style={{ animationDuration: "8s", animationDelay: "-5s" }}>
      <div
        className="rounded-xl border border-white/60 bg-white/70 p-4 shadow-card backdrop-blur-md motion-safe:animate-fade-up-delay-2"
      >
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <TrendingUp className="h-3.5 w-3.5 text-primary" />
          Bookings this month
        </div>
        <div className="mt-1 flex items-end justify-between">
          <span className="font-heading text-2xl font-bold">128</span>
          <svg viewBox="0 0 80 28" className="h-7 w-20 text-primary" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 24 L16 18 L28 20 L42 10 L54 13 L68 4 L78 6" />
          </svg>
        </div>
      </div>
      </div>
    </div>

    <p className="relative text-sm text-muted-foreground">Built for businesses across Southeast Asia.</p>
  </aside>
);

export default AuthShowcase;
